/**
 * Campfire sound, placed in the world from the fire sprites already there.
 *
 * ─── Why this reads `spriteSpawns` instead of a new authored list ───────────
 * `WorldData.spriteSpawns` is already "where the fires are" — it is the only
 * source of fire in the scene since the model-keyed auto-attach was removed.
 * A parallel list of audio positions would be a second copy of that fact,
 * and the two would drift the first time someone moved a fire in the map
 * editor and heard it crackling from where it used to be. Every spawn with
 * `sheet: 'fire'` gets an emitter; nothing else does.
 *
 * ─── Why size drives gain, and not just placement ───────────────────────────
 * The settlement's three fires are not the same fire. The camp fire is
 * 1.3 x 1.45 m; the other two are 0.8 x 0.65 and 0.7 x 0.8 — roughly a
 * quarter of the area. Emitting the same clip at the same level from all
 * three turns a settlement with one hearth and two burning barrels into one
 * with three bonfires. Gain and audible radius are both derived from the
 * sprite's own area, so the map stays the single place the size is stated.
 *
 * The square root is deliberate: area scales as the square of the linear
 * size, so gain proportional to area would drop the small fires to a quarter
 * and effectively silence them. Sqrt(area) tracks the linear size, which is
 * the perceptual match.
 *
 * ─── Why the emitters rebuild ───────────────────────────────────────────────
 * `scene.setSpriteSpawns` re-clones the whole sprite field, so the map editor
 * can hand it a different set frame to frame. Emitters built once at mount
 * would outlive the sprites they belong to — a fire deleted in the editor
 * would keep crackling. `setSpawns` is called from the same seam.
 */
import type { SpriteSpawn } from '../base-view/types';
import type { MixerHandle } from './mixer';
import { TRACKS } from './tracks';

/**
 * The camp fire's own area, m^2 — the reference every other fire is judged
 * against. Taken from `home_settlement.ts`'s `Camp Fire` spawn (1.3 x 1.45)
 * rather than invented, so the largest authored fire is the one that plays at
 * full level and nothing has to be scaled above 1.
 */
const REFERENCE_AREA = 1.3 * 1.45;

/**
 * Below this the emitter is not worth a node — a decorative ember, not a fire.
 *
 * Sub-threshold fires are SILENT ON PURPOSE. Saying so here because the whole
 * audio path swallows its own failures (a clip that 404s is ignored so a lost
 * sound cannot break the world view), which means "this fire makes no noise"
 * and "the audio is broken" look identical from the outside. In a dev build
 * the skipped ones are logged, so the two can be told apart.
 */
const MIN_GAIN = 0.2;

/**
 * Hard ceiling on concurrent emitters.
 *
 * `home_settlement` has 3 fires, but this is deliberately not gated by map and
 * the arenas are a different scale: `arena_scrapyard` authors 13 fire spawns
 * and `arena_pit` 11. Thirteen HRTF panners each looping the same 5.5s clip is
 * both real per-frame CPU and a phase-stacking problem the start-offset
 * stagger only partly hides — many copies of one short loop sum into an
 * obvious pulse however they are offset.
 *
 * The survivors are the LARGEST fires, not the nearest. Nearest-N would churn
 * the emitter set as the player walks, and every churn restarts a source —
 * audible as a crackle popping in and out. Largest-N is stable for the whole
 * mount and picks the fires that would dominate anyway.
 */
const MAX_EMITTERS = 6;

/**
 * Metres past which a fire is inaudible. Generous enough to fade in before
 * the sprite is legible, short enough that three fires across the settlement
 * do not sum into a permanent wash.
 */
const MAX_DISTANCE = 22;

export interface FireAmbienceHandle {
  /** Rebuild the emitter set. Safe to call with the same spawns repeatedly. */
  setSpawns(spawns: readonly SpriteSpawn[] | undefined): void;
  /** Move the listener. Call once a frame with the camera's world transform. */
  updateListener(pos: { x: number; y: number; z: number }, forward: { x: number; y: number; z: number }): void;
  dispose(): void;
}

interface Emitter {
  panner: PannerNode;
  gain: GainNode;
  source: AudioBufferSourceNode;
}

/**
 * Starts the fire ambience. Emitters are created as soon as the clip decodes;
 * if it fails to load the handle stays valid and silent, because losing one
 * ambient sound must not take the settlement view down with it.
 */
export function createFireAmbience(mixer: MixerHandle): FireAmbienceHandle {
  const emitters = new Map<string, Emitter>();
  let clip: AudioBuffer | null = null;
  let leadIn = 0;
  let endsAt = 0;
  let pending: readonly SpriteSpawn[] | undefined;
  let disposed = false;

  void mixer.load(TRACKS.campfire.path).then((loaded) => {
    if (disposed) return;
    clip = loaded.buffer;
    leadIn = loaded.leadIn;
    endsAt = loaded.endsAt;
    // Whatever the last requested set was — the spawns almost always arrive
    // before the decode finishes.
    rebuild(pending);
  }).catch(() => {
    // Deliberately swallowed. See the doc comment above.
  });

  const fireSpawns = (spawns: readonly SpriteSpawn[] | undefined): SpriteSpawn[] =>
    (spawns ?? []).filter((s) => s.sheet === 'fire');

  /** Perceptual level for a fire of this size, 0..1. */
  const gainFor = (s: SpriteSpawn): number =>
    Math.min(1, Math.sqrt((s.width * s.height) / REFERENCE_AREA));

  const destroy = (e: Emitter): void => {
    try { e.source.stop(); } catch { /* already stopped */ }
    e.source.disconnect();
    e.gain.disconnect();
    e.panner.disconnect();
  };

  const rebuild = (spawns: readonly SpriteSpawn[] | undefined): void => {
    pending = spawns;
    if (!clip) return;
    const candidates = fireSpawns(spawns);
    const audible = candidates.filter((s) => gainFor(s) >= MIN_GAIN);
    // Largest first, then capped — see MAX_EMITTERS. Ties break on id so the
    // set is identical across mounts rather than dependent on author order.
    const kept = [...audible]
      .sort((a, b) => (gainFor(b) - gainFor(a)) || a.id.localeCompare(b.id))
      .slice(0, MAX_EMITTERS);
    if (import.meta.env?.DEV && kept.length < candidates.length) {
      console.info(
        `[fire-ambience] ${kept.length}/${candidates.length} fires emitting — `
        + `${candidates.length - audible.length} below MIN_GAIN, `
        + `${Math.max(0, audible.length - kept.length)} over MAX_EMITTERS. `
        + 'Silent fires here are intentional, not a load failure.',
      );
    }
    const wanted = new Map(kept.map((s) => [s.id, s] as const));

    for (const [id, e] of emitters) {
      if (!wanted.has(id)) { destroy(e); emitters.delete(id); }
    }

    for (const [id, s] of wanted) {
      if (emitters.has(id)) continue;
      const ctx = mixer.ctx;
      const source = ctx.createBufferSource();
      source.buffer = clip;
      source.loop = true;
      source.loopStart = leadIn;
      source.loopEnd = endsAt;

      const gain = ctx.createGain();
      gain.gain.value = gainFor(s);

      const panner = ctx.createPanner();
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'inverse';
      // Full level out to roughly the flame's own footprint, then falling.
      panner.refDistance = 1 + s.width;
      panner.maxDistance = MAX_DISTANCE;
      panner.rolloffFactor = 1.2;
      // `positionY` offsets the BASE of the billboard, and a Sprite is placed
      // by its centre — the same `+ height / 2` the renderer applies. The ear
      // should hear the middle of the flame, not the ground under it.
      setPosition(panner, s.position.x, (s.positionY ?? 0) + s.height / 2, s.position.z);

      source.connect(gain).connect(panner).connect(mixer.busFor('effects'));
      // Stagger the start by a hash of the id. Three copies of one 5.5s clip
      // started together phase-lock into an obvious repeating pulse; offset
      // they read as three separate fires.
      const offset = leadIn + (hash(id) % 1000) / 1000 * Math.max(0.001, endsAt - leadIn);
      source.start(0, offset);
      emitters.set(id, { panner, gain, source });
    }
  };

  return {
    setSpawns: rebuild,
    updateListener: (pos, forward) => {
      const l = mixer.ctx.listener;
      // Two APIs for the same thing: the current spec exposes AudioParams,
      // older WebKit only has the setter pair. Feature-detected rather than
      // UA-sniffed.
      if (l.positionX) {
        const t = mixer.ctx.currentTime;
        l.positionX.setValueAtTime(pos.x, t);
        l.positionY.setValueAtTime(pos.y, t);
        l.positionZ.setValueAtTime(pos.z, t);
        l.forwardX.setValueAtTime(forward.x, t);
        l.forwardY.setValueAtTime(forward.y, t);
        l.forwardZ.setValueAtTime(forward.z, t);
        l.upX.setValueAtTime(0, t);
        l.upY.setValueAtTime(1, t);
        l.upZ.setValueAtTime(0, t);
      } else {
        const legacy = l as unknown as {
          setPosition(x: number, y: number, z: number): void;
          setOrientation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number): void;
        };
        legacy.setPosition(pos.x, pos.y, pos.z);
        legacy.setOrientation(forward.x, forward.y, forward.z, 0, 1, 0);
      }
    },
    dispose: () => {
      disposed = true;
      for (const e of emitters.values()) destroy(e);
      emitters.clear();
    },
  };
}

/** Same feature-detection as the listener, for the panner's own position. */
function setPosition(panner: PannerNode, x: number, y: number, z: number): void {
  if (panner.positionX) {
    panner.positionX.value = x;
    panner.positionY.value = y;
    panner.positionZ.value = z;
  } else {
    (panner as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
  }
}

/** Stable small integer from an id, so the stagger is the same every mount. */
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
